// client/src/coms/views/canvas_parts/canvasRoomUtils.js
import { useLayoutEffect, useRef, useState } from 'react';

export const DEFAULT_DOCUMENT_SIZE = { width: 1280, height: 720 };
export const MIN_DOCUMENT_SIZE = 1;
export const MAX_DOCUMENT_SIZE = 8192;


export function pickCanvasId(canvas) {
  const n = Number(canvas && canvas.id);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

export function pickCanvasName(canvas) {
  const s = String((canvas && canvas.name) || '').trim();
  return s || 'Untitled Canvas';
}

export function pickOwnerId(canvas) {
  const n = Number(canvas && canvas.owner_user_id);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

export function pickParticipantIds(canvas) {
  const raw = canvas && canvas.participant_user_ids;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((v) => Number(v))
    .filter((v) => Number.isInteger(v) && v > 0);
}

export function pickMaxParticipants(canvas) {
  const n = Number(canvas && canvas.max_participants);
  if (Number.isInteger(n) && n > 0) return n;
  return 4;
}

export function clampDocumentDimension(value, fallback = 1280) {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(MIN_DOCUMENT_SIZE, Math.min(MAX_DOCUMENT_SIZE, Math.round(n)));
}

export function pickCanvasDocumentSize(canvas) {
  return {
    width: clampDocumentDimension(canvas && canvas.canvas_width, DEFAULT_DOCUMENT_SIZE.width),
    height: clampDocumentDimension(canvas && canvas.canvas_height, DEFAULT_DOCUMENT_SIZE.height),
  };
}

export function formatDateTime(value) {
  try {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function clampBrushSize(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 12;
  return Math.max(1, Math.min(1600, Math.round(n)));
}

export function normaliseHexColor(value, fallback = '#000000') {
  const raw = String(value || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toLowerCase();
  return fallback;
}

export default function useCanvasStageSize(containerRef, initialSize = { width: 800, height: 500 }) {
  const [stageSize, setStageSize] = useState(initialSize);
  const frameRef = useRef(0);

  useLayoutEffect(() => {
    let ro = null;

    const measure = () => {
      const el = containerRef.current;
      if (!el) return;

      const style = typeof window !== 'undefined' ? window.getComputedStyle(el) : null;
      const paddingX = style ? (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0) : 0;
      const paddingY = style ? (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0) : 0;
      const width = Math.max(320, Math.floor(el.clientWidth - paddingX - 2));
      const height = Math.max(320, Math.floor(el.clientHeight - paddingY - 2));

      setStageSize((prev) => {
        if (prev.width === width && prev.height === height) return prev;
        return { width, height };
      });
    };

    const scheduleMeasure = () => {
      if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
        measure();
        return;
      }

      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = 0;
        measure();
      });
    };

    measure();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', scheduleMeasure);
    }

    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      ro = new ResizeObserver(scheduleMeasure);
      ro.observe(containerRef.current);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', scheduleMeasure);
        if (frameRef.current) {
          window.cancelAnimationFrame(frameRef.current);
          frameRef.current = 0;
        }
      }
      if (ro) ro.disconnect();
    };
  }, [containerRef]);

  return stageSize;
}
